import type { CampaignTimeStatus } from './campaignStatusHelper';

// De publicatie-cron draait elke 5 minuten; drempels daarop afgestemd
// (3 gemiste runs = vertraagd, een uur = te laat).
const PROCESSING_THRESHOLD_MINUTES = 15;
const DELAYED_THRESHOLD_MINUTES = 60;

export function getSocialPostTimeStatus(
  scheduledAt: string | null,
  status: string,
  upStatus: string | null = null,
  retryCount: number | null = null
): CampaignTimeStatus {
  const defaultStatus: CampaignTimeStatus = {
    isOverdue: false,
    processingStatus: 'on-time',
    minutesLate: 0,
    hoursLate: 0,
    daysLate: 0,
    formattedLateness: '',
    statusLabel: '',
  };

  if (!scheduledAt || status !== 'scheduled') {
    return defaultStatus;
  }

  // Async publicatie (video) loopt al bij Upload-Post; de poll-cron rondt af.
  if (upStatus === 'processing') {
    return {
      ...defaultStatus,
      processingStatus: 'processing',
      statusLabel: 'Wordt verwerkt',
    };
  }

  const now = new Date();
  const scheduled = new Date(scheduledAt);

  if (scheduled > now) {
    return defaultStatus;
  }

  const diffMs = now.getTime() - scheduled.getTime();
  const minutesLate = Math.floor(diffMs / (1000 * 60));
  const hoursLate = Math.floor(diffMs / (1000 * 60 * 60));
  const daysLate = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutesLate >= DELAYED_THRESHOLD_MINUTES) {
    let formattedLateness = '';
    if (daysLate > 0) {
      formattedLateness = `${daysLate} dag${daysLate > 1 ? 'en' : ''} te laat`;
    } else if (hoursLate > 0) {
      formattedLateness = `${hoursLate} uur te laat`;
    } else {
      formattedLateness = `${minutesLate} minuten te laat`;
    }

    return {
      isOverdue: true,
      processingStatus: 'overdue',
      minutesLate,
      hoursLate,
      daysLate,
      formattedLateness,
      statusLabel: 'Te laat',
    };
  }

  // Eerdere poging mislukt; de cron probeert het binnen 5 minuten opnieuw.
  if (upStatus === 'failed' && (retryCount ?? 0) > 0) {
    return {
      ...defaultStatus,
      processingStatus: 'delayed',
      minutesLate,
      statusLabel: 'Opnieuw proberen',
    };
  }

  if (minutesLate < PROCESSING_THRESHOLD_MINUTES) {
    return {
      ...defaultStatus,
      processingStatus: 'processing',
      minutesLate,
      statusLabel: 'Wordt verwerkt',
    };
  }

  return {
    ...defaultStatus,
    processingStatus: 'delayed',
    minutesLate,
    formattedLateness: `${minutesLate} min vertraagd`,
    statusLabel: 'Vertraagd',
  };
}
